// react
import { SyntheticEvent, useContext, useState } from 'react';

// types
import {
	Act,
	Comment,
	Document,
	Ifr,
	Pagination,
	Payment,
	Transaction,
	TransactionAugmented,
	WithdrawalApplication,
} from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { TabContext, TabPanel } from '@mui/lab';
import {
	Box,
	BoxProps,
	Chip,
	Grid,
	TableCell,
	TableFooter,
	TableRow,
	Typography,
} from '@mui/material';

// ui
import { TableProps } from '../../ui/Table/Table';
import { TableRowBgColor } from '../../ui/Table/TableRow/TableRow';
import { TabProps, Tabs } from '../../ui/Tabs/Tabs';
import { Tags } from '../../ui/Tags/Tags';

// tables
import { TableBasicActs } from '../../tables/basic/TableBasicActs/TableBasicActs';
import { TableBasicComments } from '../../tables/basic/TableBasicComments/TableBasicComments';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicIfrs } from '../../tables/basic/TableBasicIfrs/TableBasicIfrs';
import {
	TableBasicPayments,
	TableBasicPaymentsProps,
} from '../../tables/basic/TableBasicPayments/TableBasicPayments';
import { TableBasicWithdrawalApplications } from '../../tables/basic/TableBasicWithdrawalApplications/TableBasicWithdrawalApplications';

// utils
import { defaultPagination } from '../../../utils/defaults';
import { enhanceComments, enhanceDocuments } from '../../../utils/transactions';

// theme
import { theme } from '../../../theme';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
const transactionTabs = [
	'activities',
	'applied',
	'comments',
	'documents',
	'financing',
	'ifrs',
	'tags',
	'withdrawalApplications',
];

export type TabsTransactionTabs = (typeof transactionTabs)[number];

export interface TabsProps {
	financing: {
		TableBasicPaymentsProps: Partial<TableBasicPaymentsProps>;
	};
}

export interface TabsTransactionProps {
	BoxProps?: BoxProps;
	TableProps?: Omit<TableProps, 'data' | 'headers'>;
	tabs?: TabsTransactionTabs[];
	transaction: TransactionAugmented;
}

export const TabsTransaction = ({
	BoxProps,
	TableProps,
	tabs: tabsProp = transactionTabs,
	transaction,
}: TabsTransactionProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogDateEnd = searchParams.get('dialogDateEnd');
	const paramDialogDateStart = searchParams.get('dialogDateStart');

	// vars
	const isAdvance = transaction.type === 'ADVANCE';
	const isCashBenefit = transaction.type === 'CASH_BENEFIT';
	const isDirectPayment = transaction.type === 'DIRECT';
	const isOpExpense = transaction.type === 'OPEX';
	const isPayment = transaction.type === 'PAYMENT';
	const isReimbursement = transaction.type === 'REIMBURSEMENT';
	const payments: Payment[] = [];
	const tabs: TabProps[] = [];
	const tabsProps: TabsProps = {
		financing: {
			TableBasicPaymentsProps: {
				headersIncluded: [
					'account',
					'amount',
					'hash',
					'recorded',
					'reimbursed',
					'source',
					'status',
					'tax',
					'total',
				],
				...TableProps,
			},
		},
	};

	if (transaction.type === 'PAYMENT_REFUND') {
		tabsProps.financing.TableBasicPaymentsProps = {
			...tabsProps.financing.TableBasicPaymentsProps,
			headersIncluded: ['amount', 'date', 'hash', 'recorded', 'source', 'total', 'type', 'updated'],
			...TableProps,
		};
	}

	let hasReadPermission = false;

	if (
		(isAdvance && userProjectPermissions?.payment?.read) ||
		(isCashBenefit && userProjectPermissions?.cashBenefit?.read) ||
		(isDirectPayment && userProjectPermissions?.payment?.read) ||
		(isOpExpense && userProjectPermissions?.opEx?.read) ||
		(isPayment && userProjectPermissions?.payment?.read)
	) {
		hasReadPermission = true;
	}

	if (tabsProp.includes('financing')) {
		tabs.push({
			label: t('financing'),
			value: 'financing',
		});
	}

	if (tabsProp.includes('applied') && isReimbursement) {
		tabs.push({
			label: t('applied'),
			value: 'applied',
		});
	}

	if (tabsProp.includes('documents') && userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (
		tabsProp.includes('activities') &&
		isPayment &&
		userProjectPermissions?.act?.read &&
		featureFlags['ACTIVITIES']
	) {
		tabs.push({
			label: t('activities'),
			value: 'activities',
		});
	}

	if (tabsProp.includes('comments') && userProjectPermissions?.comment?.read) {
		tabs.push({
			label: t('comments'),
			value: 'comments',
		});
	}

	if (tabsProp.includes('withdrawalApplications') && isDirectPayment) {
		tabs.push({
			label: t('withdrawalApplication'),
			value: 'withdrawalApplications',
		});
	}

	if (tabsProp.includes('ifrs') && hasReadPermission && featureFlags['IFRS']) {
		tabs.push({
			label: t('reports'),
			value: 'ifrs',
		});
	}

	if (tabsProp.includes('tags') && userProjectPermissions?.tag?.read) {
		tabs.push({
			label: t('tags'),
			value: 'tags',
		});
	}

	// state
	const [acts, setActs] = useState<Act[]>([]);
	const [comments, setComments] = useState<Comment[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [withdrawalApplications, setWithdrawalApplications] = useState<WithdrawalApplication[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [tabValue, setTabValue] = useState(tabs[0].value);
	const [transactionsApplied, setTransactionsApplied] = useState<Transaction[]>([]);

	// filter new comments
	const commentsNew = comments.filter((c) => c.isNew);

	// filter new documents
	const documentsNew = documents.filter((d) => d.isNew);

	const chipNew = <Chip color="error" label={t('new')} size="small" variant="outlined" />;

	if (documentsNew.length) {
		const tabDocs = tabs.find((tab) => tab.value === 'documents');

		if (tabDocs) {
			tabDocs.label = (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>{tabDocs.label}</Grid>
					<Grid item>{chipNew}</Grid>
				</Grid>
			);
		}
	}

	if (commentsNew.length) {
		const tabComments = tabs.find((tab) => tab.value === 'comments');

		if (tabComments) {
			tabComments.label = (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>{tabComments.label}</Grid>
					<Grid item>{chipNew}</Grid>
				</Grid>
			);
		}
	}

	// add transaction to each payment
	for (let p = 0; p < transaction.payments.length; p++) {
		payments.push({
			...transaction.payments[p],
			transaction,
		});
	}

	// add refund payments as negative rows
	const refunds = (transaction.refunds || []) as Transaction[];
	for (let r = 0; r < refunds.length; r++) {
		const refund = refunds[r];
		if (typeof refund === 'object' && refund.payments?.length) {
			for (let p = 0; p < refund.payments.length; p++) {
				payments.push({
					...refund.payments[p],
					amount: -Math.abs(refund.payments[p].amount),
					transaction: refund,
				});
			}
		}
	}

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleChangeDocumentPage = (page: number) => {
		setPaginationDocuments((prev) => ({
			...prev,
			page,
		}));
	};

	// determine if transactions are already populated as objects
	const transactionsPopulated = ((transaction.transactions || []) as Transaction[]).filter(
		(t) => typeof t === 'object'
	) as Transaction[];
	const transactionIds = ((transaction.transactions || []) as number[]).filter(
		(t) => typeof t === 'number'
	) as number[];

	// fetch transactions applied to reimbursement (only when not already populated)
	let urlFetchTransactionsApplied = `${process.env.REACT_APP_API_URL}/transactions?id=${transactionIds.join(',')}&populate=expenseType,payments,payments.conversions,payments.loan,payments.reimbursed.conversions`;

	if (paramDialogDateStart) {
		urlFetchTransactionsApplied += `&dateStart=${paramDialogDateStart}`;
	}
	if (paramDialogDateEnd) {
		urlFetchTransactionsApplied += `&dateEnd=${paramDialogDateEnd}`;
	}
	const { isLoading: isLoadingTransactionsApplied } = useFetch({
		isEnabled: Boolean(isReimbursement && transactionIds.length && tabValue === 'applied'),
		url: urlFetchTransactionsApplied,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactionsApplied(res.data);
			}
		},
	});

	const transactionsAppliedResolved = transactionsPopulated.length
		? transactionsPopulated
		: transactionsApplied;

	// define applied payments
	const paymentsApplied: Payment[] = [];
	for (let i = 0; i < transactionsAppliedResolved.length; i++) {
		for (let j = 0; j < transactionsAppliedResolved[i].payments.length; j++) {
			const p = transactionsAppliedResolved[i].payments[j];
			if (p.reimbursed?.amount) {
				paymentsApplied.push({
					...p,
					transaction: transactionsAppliedResolved[i],
				});
			}
		}
	}

	// fetch acts
	const { isLoading: isLoadingActs } = useFetch({
		isEnabled: Boolean(transaction && isPayment && tabValue === 'activities'),
		url: `${process.env.REACT_APP_API_URL}/acts?transaction=${transaction?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}
		},
	});

	// fetch comments
	const { isLoading: isLoadingComments } = useFetch({
		isEnabled: Boolean(transaction && tabValue === 'comments'),
		url: `${process.env.REACT_APP_API_URL}/comments?transaction=${transaction?.id}&populate=user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComments(enhanceComments({ comments: res.data }));
			}
		},
	});

	// fetch documents
	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(transaction && tabValue === 'documents'),
		url: `${process.env.REACT_APP_API_URL}/documents?transaction=${transaction?.id}&limit=${paginationDocuments.perPage}&page=${paginationDocuments.page}&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const documentsEnhanced = enhanceDocuments({ documents: res.data });
				setDocuments(documentsEnhanced);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// fetch withdrawal applications
	const { isLoading: isLoadingWithdrawalApplications } = useFetch({
		isEnabled: Boolean(transaction && isDirectPayment && tabValue === 'withdrawalApplications'),
		url: `${process.env.REACT_APP_API_URL}/transactions/${transaction?.id}?fields=withdrawalApplication&populate=withdrawalApplication`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.length) {
				const withdrawalApplication = res.data[0].withdrawalApplication;
				setWithdrawalApplications(
					withdrawalApplication ? [withdrawalApplication as WithdrawalApplication] : []
				);
			}
		},
	});

	return (
		<Box {...BoxProps}>
			<TabContext value={tabValue}>
				<Tabs
					BoxProps={{
						sx: {
							paddingTop: {
								lg: theme.spacing(3),
							},
						},
					}}
					hasBorder={false}
					onChange={handleChangeTab}
					tabs={tabs}
					value={tabValue}
				/>
				{tabsProp.includes('financing') && (
					<TabPanel value="financing">
						<TableBasicPayments
							data={payments}
							transaction={transaction}
							{...tabsProps.financing.TableBasicPaymentsProps}
						/>
					</TabPanel>
				)}
				{tabsProp.includes('applied') && isReimbursement && (
					<TabPanel value="applied">
						<TableBasicPayments
							{...TableProps}
							data={paymentsApplied}
							headersIncluded={['date', 'hash', 'reimbursed', 'source', 'total', 'transaction']}
							isLoading={isLoadingTransactionsApplied}
							transaction={transaction}
						/>
					</TabPanel>
				)}
				{tabsProp.includes('documents') && (
					<TabPanel value="documents">
						<TableBasicDocs
							data={documents}
							isLoading={isLoadingDocs}
							refetchDocuments={refetchDocuments}
							pagination={paginationDocuments}
							PaginationProps={{
								onChange: (_, page) => handleChangeDocumentPage(page),
							}}
							transaction={transaction}
						/>
					</TabPanel>
				)}
				{tabsProp.includes('activities') && isPayment && (
					<TabPanel value="activities">
						<TableBasicActs data={acts} isLoading={isLoadingActs} />
					</TabPanel>
				)}
				{tabsProp.includes('comments') && (
					<TabPanel value="comments">
						<TableBasicComments data={comments} isLoading={isLoadingComments} />
					</TabPanel>
				)}
				{tabsProp.includes('withdrawalApplications') && isDirectPayment && (
					<TabPanel value="withdrawalApplications">
						<TableBasicWithdrawalApplications
							data={withdrawalApplications}
							isLoading={isLoadingWithdrawalApplications}
						/>
					</TabPanel>
				)}
				{tabsProp.includes('ifrs') && (
					<TabPanel value="ifrs">
						<TableBasicIfrs
							data={transaction.ifrs || []}
							getRowBgColor={(ifr: Ifr) => {
								let bgColor: TableRowBgColor = 'transparent';
								// color row yellow if transaction created date is after ifr end date
								if (transaction.created && new Date(transaction.created) > new Date(ifr.dateEnd)) {
									bgColor = 'yellow';
								}
								return bgColor;
							}}
							sxBox={{
								borderBottomLeftRadius: 8,
								borderBottomRightRadius: 8,
							}}
							transaction={transaction}>
							<TableFooter>
								<TableRow
									sx={{
										backgroundColor: theme.palette.brand.blue[100],
									}}>
									<TableCell colSpan={4}>
										<Typography color="brand.blue.700" component="span" variant="caption">
											<Typography color="error" component="span">
												*
											</Typography>{' '}
											{t('table.basic.ifrs.footer')}
										</Typography>
									</TableCell>
								</TableRow>
							</TableFooter>
						</TableBasicIfrs>
					</TabPanel>
				)}
				{tabsProp.includes('tags') && (
					<TabPanel value="tags">
						<Tags tags={transaction.tags} />
					</TabPanel>
				)}
			</TabContext>
		</Box>
	);
};
